import { formatCalculatorAmount, getQuoteValidThrough } from '@/services/pricingCalculatorService'
import { logger } from '@/services/loggerService'

// Anthem brand palette — the quote is branded to Anthem (not the client
// company), so these are fixed and intentionally do NOT use --bulma-primary.
const ACCENT = '#3CB4E5' // Anthem blue ("IMPACT" accent on the cover)
const NAVY = '#1f2933' // headline / wordmark
const BODY = '#4a4f57' // body copy
const MUTED = '#8a9099' // eyebrow / footnotes

// Rasterized cover (page 1) + footer strip cropped from the cover bottom. Both
// live in /public so they serve same-origin (no CORS). The footer is the actual
// cover pixels (≡ Anthem logomark + AnthemContent.com), drawn full-bleed at the
// page-2 bottom so it is pixel-identical to page 1 instead of a fake text copy.
const COVER_SRC = '/assets/pdf/quote-cover.jpg'
const FOOTER_SRC = '/assets/pdf/quote-footer.png'

// Horizontal page margin matched to the cover (logo/headline left edge),
// so page-2 content aligns with the cropped footer logomark.
const MARGIN_X = 0.45

// Letter page in inches (jsPDF unit: 'in')
const PAGE_W = 8.5
const PAGE_H = 11

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

function formatLongDate(date) {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function buildLineItemsHtml(lineItems, currency) {
  return (lineItems || [])
    .map((item) => {
      const detail = item.detail
        ? `<span class="qpdf-line-detail">${escapeHtml(item.detail)}</span>`
        : ''
      return `
        <div class="qpdf-line">
          <div class="qpdf-line-text">
            <span class="qpdf-line-title">${escapeHtml(item.title || item.label)}</span>
            ${detail}
          </div>
          <span class="qpdf-line-amount">${formatCalculatorAmount(item.amount, currency)}</span>
        </div>`
    })
    .join('')
}

/**
 * Build the page-2 breakdown element (off-screen). jsPDF places it on its own
 * page, so this is a single Letter-sized frame styled to match the cover.
 */
function buildBreakdownElement({ projectName, companyName, lineItems, total, currency, validThrough }) {
  const title = projectName?.trim() || 'Project Proposal'
  const company = companyName?.trim() || ''

  const el = document.createElement('div')
  el.style.cssText = `position:fixed;left:-99999px;top:0;width:${PAGE_W}in;height:${PAGE_H}in;background:#fff;`
  el.innerHTML = `
    <div class="qpdf-breakdown">
      <style>
        .qpdf-breakdown {
          font-family: 'Mona Sans', system-ui, -apple-system, sans-serif;
          color: ${BODY};
          width: ${PAGE_W}in;
          height: ${PAGE_H}in;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          padding: 0.85in ${MARGIN_X}in 0;
          background: #fff;
        }
        .qpdf-eyebrow {
          font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
          color: ${MUTED}; font-weight: 600; margin: 0 0 6px;
        }
        .qpdf-title {
          font-size: 34px; line-height: 1.05; font-weight: 800; color: ${NAVY};
          letter-spacing: -0.01em; margin: 0;
        }
        .qpdf-company { font-size: 14px; font-weight: 500; color: ${BODY}; margin: 8px 0 0; }
        .qpdf-rule { width: 56px; height: 4px; background: ${ACCENT}; border-radius: 2px; margin: 16px 0 28px; }

        .qpdf-line {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 16px; padding: 13px 0; border-bottom: 1px solid #e8eaed;
        }
        .qpdf-line-text { display: flex; flex-direction: column; }
        .qpdf-line-title { font-size: 14px; font-weight: 700; color: ${NAVY}; }
        .qpdf-line-detail { font-size: 11.5px; color: ${MUTED}; margin-top: 2px; }
        .qpdf-line-amount { font-size: 14px; color: ${NAVY}; font-weight: 600; white-space: nowrap; }

        .qpdf-total {
          display: flex; align-items: baseline; justify-content: space-between;
          margin-top: 18px; padding-top: 16px; border-top: 2px solid ${NAVY};
        }
        .qpdf-total-label {
          font-size: 15px; font-weight: 700; color: ${NAVY};
          text-transform: uppercase; letter-spacing: 0.04em;
        }
        .qpdf-total-amount { font-size: 28px; font-weight: 800; color: ${ACCENT}; white-space: nowrap; }

        .qpdf-note { margin-top: 20px; font-size: 11px; color: ${MUTED}; line-height: 1.5; }
      </style>

      <p class="qpdf-eyebrow">Project Proposal</p>
      <h1 class="qpdf-title">${escapeHtml(title)}</h1>
      ${company ? `<p class="qpdf-company">${escapeHtml(company)}</p>` : ''}
      <div class="qpdf-rule"></div>

      <div class="qpdf-lines">${buildLineItemsHtml(lineItems, currency)}</div>

      <div class="qpdf-total">
        <span class="qpdf-total-label">Total</span>
        <span class="qpdf-total-amount">${formatCalculatorAmount(total, currency)}</span>
      </div>

      <p class="qpdf-note">
        Proposal valid through ${formatLongDate(validThrough)}. Final pricing is
        confirmed when your order is placed.
      </p>
    </div>
  `
  return el
}

/**
 * Generate and download a 2-page branded quote PDF.
 * Page 1 is the Anthem cover image; page 2 is the pricing breakdown.
 *
 * Drives jsPDF directly (addImage → addPage → addImage) rather than relying on
 * html2pdf's auto-pagination, which guarantees exactly two pages.
 *
 * @param {Object} params
 * @param {string} params.projectName
 * @param {string} [params.companyName] - shown as a sub-line under the title
 * @param {Array<{title?: string, label?: string, detail?: string, amount: number}>} params.lineItems
 * @param {number} params.total
 * @param {string} [params.currency='USD']
 * @param {*} [params.createdAt] - draft creation timestamp (drives 30-day validity)
 */
export async function generateQuotePdf({
  projectName,
  companyName,
  lineItems,
  total,
  currency = 'USD',
  createdAt = null
}) {
  const [{ jsPDF }, html2canvas] = await Promise.all([
    import('jspdf'),
    import('html2canvas').then((m) => m.default)
  ])

  const pdf = new jsPDF({ unit: 'in', format: 'letter', orientation: 'portrait' })

  // Page 1 — cover image, full bleed
  const cover = await loadImage(COVER_SRC)
  pdf.addImage(cover, 'JPEG', 0, 0, PAGE_W, PAGE_H)

  // Page 2 — pricing breakdown, rendered from off-screen HTML
  const validThrough = getQuoteValidThrough(createdAt)
  const el = buildBreakdownElement({ projectName, companyName, lineItems, total, currency, validThrough })
  document.body.appendChild(el)
  try {
    // Ensure the brand webfont is ready so html2canvas captures it
    if (document.fonts?.ready) await document.fonts.ready
    const canvas = await html2canvas(el.firstElementChild, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff'
    })
    pdf.addPage('letter', 'portrait')
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, PAGE_W, PAGE_H)

    // Real footer (cropped cover pixels), full-bleed flush to the page bottom —
    // pixel-identical to page 1. PNG keeps the logomark crisp.
    const footer = await loadImage(FOOTER_SRC)
    const footerH = PAGE_W * (footer.naturalHeight / footer.naturalWidth)
    pdf.addImage(footer, 'PNG', 0, PAGE_H - footerH, PAGE_W, footerH)
  } finally {
    document.body.removeChild(el)
  }

  const safeName = (projectName?.trim() || 'Anthem').replace(/[^\w\s-]/g, '').trim()
  pdf.save(`${safeName} Proposal.pdf`)

  logger.info('[quotePdfService] Quote PDF generated', { projectName: safeName })
}
